import { pushGa4UserId } from '@web-app/lib/analytics/ga4UserId'
import { awsWafStageFetch } from '@web-app/lib/awsWafStage'
import type { ServerFieldError } from '@web-app/lib/errors/serverFieldErrors'

// The browser's one door to the auth routes (rule: web-app-auth-client).
// Responses that write the auth cookie are serialized by one origin-wide
// lock: the jar is last-write-wins, so a late mint must not follow a sign-out.
// Each sign-in function owns its route's whole contract, request body to
// response body, and answers an outcome; callers only choose copy.

// Keep the deployed name: tabs on different frontend versions share it.
const AUTH_COOKIE_LOCK = 'atm-mint'
const MINT_TIMEOUT_MS = 4_000
const MINT_ATTEMPTS = 3
const FORCE_REFRESH_HEADER = 'x-atm-force-refresh'

type SignedIn = { kind: 'signed-in'; destination: string }
// No answer at all: offline, blocked, aborted.
type Unreachable = { kind: 'unreachable' }

export type SignInOutcome =
  | SignedIn
  // 401. Vague on the wire on purpose, so the copy stays vague too.
  | { kind: 'rejected' }
  | { kind: 'failed' }
  | Unreachable

export type RegisterOutcome =
  | SignedIn
  // The route said no: the backend's field errors when it named fields, its
  // message when it had one.
  | {
      kind: 'rejected'
      message?: string
      errors: ReadonlyArray<ServerFieldError>
    }
  | Unreachable

export type SignOutOutcome =
  | { kind: 'signed-out' }
  | { kind: 'failed' }
  | Unreachable

// Held until the response is stored. async on purpose: newer DOM typings make
// request() generic, so a plain arrow would return Promise<Promise<T>>.
const withAuthCookieLock = async <T>(
  writeCookie: () => Promise<T>,
): Promise<T> =>
  typeof navigator === 'undefined' || !navigator.locks
    ? writeCookie()
    : navigator.locks.request(AUTH_COOKIE_LOCK, writeCookie)

// null when the route never answered. Only the round trip is caught: a
// malformed answer is a defect and throws.
const answerUnderLock = async (
  send: () => Promise<Response>,
): Promise<Response | null> => {
  try {
    return await withAuthCookieLock(send)
  } catch {
    return null
  }
}

const postJson = (path: string, body: unknown): Promise<Response> =>
  awsWafStageFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

// What the sign-in and register routes answer on success. The Google tag
// learns who signed in here, before any caller can navigate.
const signedIn = async (answer: Response): Promise<SignedIn> => {
  const { destination, userId }: { destination: string; userId: string } =
    await answer.json()
  pushGa4UserId(userId)
  return { kind: 'signed-in', destination }
}

const signIn = async (path: string, body: unknown): Promise<SignInOutcome> => {
  const answer = await answerUnderLock(() => postJson(path, body))
  if (!answer) return { kind: 'unreachable' }
  if (answer.ok) return signedIn(answer)
  return { kind: answer.status === 401 ? 'rejected' : 'failed' }
}

export const signInWithEmail = (body: {
  email: string
  password: string
  redirect?: string
}): Promise<SignInOutcome> => signIn('/api/auth/login-with-email', body)

export const signInWithSmsCode = (body: {
  phone: string
  code: string
  redirect?: string
}): Promise<SignInOutcome> => signIn('/api/auth/login-with-sms-code', body)

export const register = async (body: {
  phone: string
  email: string
  password: string
  redirect?: string
}): Promise<RegisterOutcome> => {
  const answer = await answerUnderLock(() =>
    postJson('/api/auth/register', body),
  )
  if (!answer) return { kind: 'unreachable' }
  if (answer.ok) return signedIn(answer)
  // 400 carries a message, 422 adds the backend's field errors, 500 is the
  // wrapper's message; a body that isn't JSON reads as neither.
  const { message, errors }: { message?: string; errors?: ServerFieldError[] } =
    await answer.json().catch(() => ({}))
  return { kind: 'rejected', message, errors: errors ?? [] }
}

export const logout = async (): Promise<SignOutOutcome> => {
  const answer = await answerUnderLock(() =>
    awsWafStageFetch('/api/auth/logout', { method: 'POST' }),
  )
  if (!answer) return { kind: 'unreachable' }
  if (!answer.ok) return { kind: 'failed' }
  // The tag must stop stamping the old user on the next hit.
  pushGa4UserId(null)
  return { kind: 'signed-out' }
}

// Consent carries the page's own access token; the cookie is untouched. Raw
// Response: the MCP page reads the statuses itself.
export const authorizeMcp = (
  body: { action: 'allow' | 'deny'; accessToken?: string } & Record<
    string,
    unknown
  >,
): Promise<Response> => postJson('/api/auth/oauth/mcp-authorize', body)

// Dead-man switch: a hung mint must not hold the lock forever.
const mintTimeout = (): AbortSignal | undefined =>
  typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
    ? AbortSignal.timeout(MINT_TIMEOUT_MS)
    : undefined

// One lock across all attempts; 4 s timeouts keep three tries inside the
// backend's 10 s replay window (prod mint p99.9 is 4.63 s). 401 is final.
// Raw Response: the token refresh path reads the statuses itself.
export const mint = ({
  forceRefresh = false,
}: { forceRefresh?: boolean } = {}): Promise<Response> =>
  withAuthCookieLock(async () => {
    let lastResponse: Response | undefined
    let lastError: unknown
    for (let attempt = 0; attempt < MINT_ATTEMPTS; attempt += 1) {
      try {
        const response = await awsWafStageFetch('/api/auth/token', {
          method: 'POST',
          signal: mintTimeout(),
          headers: forceRefresh ? { [FORCE_REFRESH_HEADER]: '1' } : undefined,
        })
        if (response.ok || response.status === 401) return response
        lastResponse = response
      } catch (error) {
        lastError = error
      }
    }
    if (lastResponse) return lastResponse
    throw lastError
  })
